import { registerSW } from 'virtual:pwa-register';

const UPDATE_INTERVAL_MS = 60_000;
const SAVE_BEFORE_RELOAD_TIMEOUT_MS = 2_000;

export interface ServiceWorkerRegistrationCallbacks {
  onOfflineReady?: () => void;
  beforeReload?: () => Promise<void>;
}

export interface ServiceWorkerUpdateEnvironment {
  isOnline(): boolean;
  isVisible(): boolean;
  addWindowListener(type: 'focus' | 'online', listener: () => void): () => void;
  addDocumentListener(type: 'visibilitychange', listener: () => void): () => void;
  setUpdateInterval(listener: () => void): () => void;
}

interface ServiceWorkerUpdater {
  update(): Promise<unknown>;
}

let stopUpdateMonitoring: (() => void) | undefined;

function createBrowserUpdateEnvironment(): ServiceWorkerUpdateEnvironment {
  return {
    isOnline: () => navigator.onLine,
    isVisible: () => document.visibilityState === 'visible',
    addWindowListener(type, listener) {
      window.addEventListener(type, listener);
      return () => window.removeEventListener(type, listener);
    },
    addDocumentListener(type, listener) {
      document.addEventListener(type, listener);
      return () => document.removeEventListener(type, listener);
    },
    setUpdateInterval(listener) {
      const interval = window.setInterval(listener, UPDATE_INTERVAL_MS);
      return () => window.clearInterval(interval);
    },
  };
}

/** Keep an online, visible client on the current deployed service worker. */
export function monitorServiceWorkerUpdates(
  registration: ServiceWorkerUpdater,
  environment: ServiceWorkerUpdateEnvironment = createBrowserUpdateEnvironment(),
): () => void {
  let activeUpdate: Promise<unknown> | null = null;

  const checkForUpdate = () => {
    if (!environment.isOnline() || !environment.isVisible() || activeUpdate) return;
    activeUpdate = registration
      .update()
      .catch(() => undefined)
      .finally(() => {
        activeUpdate = null;
      });
  };

  const removeFocusListener = environment.addWindowListener('focus', checkForUpdate);
  const removeOnlineListener = environment.addWindowListener('online', checkForUpdate);
  const removeVisibilityListener = environment.addDocumentListener(
    'visibilitychange',
    checkForUpdate,
  );
  const clearUpdateInterval = environment.setUpdateInterval(checkForUpdate);

  checkForUpdate();

  return () => {
    removeFocusListener();
    removeOnlineListener();
    removeVisibilityListener();
    clearUpdateInterval();
  };
}

/** Save accepted actions before the new service worker reloads the page. */
export async function reloadAfterServiceWorkerUpdate(
  beforeReload: (() => Promise<void>) | undefined,
  reload: () => void,
): Promise<void> {
  const save = Promise.resolve()
    .then(() => beforeReload?.())
    .catch(() => undefined);
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timeoutId = globalThis.setTimeout(resolve, SAVE_BEFORE_RELOAD_TIMEOUT_MS);
  });
  await Promise.race([save, timeout]);
  if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  reload();
}

/** Register Workbox and force every online open tab onto each deployed build. */
export function registerAppServiceWorker(
  callbacks: ServiceWorkerRegistrationCallbacks = {},
): void {
  if (import.meta.env.SSR || typeof window === 'undefined') {
    return;
  }

  let reloadStarted = false;
  registerSW({
    immediate: true,
    onRegisteredSW(_scriptUrl, registration) {
      if (!registration) return;
      stopUpdateMonitoring?.();
      stopUpdateMonitoring = monitorServiceWorkerUpdates(registration);
    },
    onNeedReload() {
      if (reloadStarted) return;
      reloadStarted = true;
      void reloadAfterServiceWorkerUpdate(
        callbacks.beforeReload,
        () => window.location.reload(),
      );
    },
    onOfflineReady() {
      callbacks.onOfflineReady?.();
    },
  });
}
